import { LocalStorageService } from '../../../services/local-storage.service';
import { Component, Input, OnDestroy } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { WorkspaceLayoutService } from '../../../services/workspace-layout.service';
import { WorkspaceOpenMode, workspaceNavigationInfo } from '../../../services/workspace-route';
import { map } from 'rxjs/operators';

import { MapFeature, MapGeometryType } from '../../core/map-feature';
import { MapFeatureManagerService } from '../../core/map-feature-manager.service';

interface MapFeatureMenuItem {
  readonly key: string;
  readonly page: string | null;
  readonly name: string;
  readonly icon: string;
  readonly iconColor: string | null;
  readonly enabled: boolean;
  readonly loading: boolean;
  readonly count: number | null;
}

interface MapFeatureMenuGroup {
  readonly geometryType: MapGeometryType;
  readonly name: string;
  readonly features: readonly MapFeatureMenuItem[];
}

interface MapFeatureMenuView {
  readonly groups: readonly MapFeatureMenuGroup[];
  readonly visibleFeatures: readonly MapFeatureMenuItem[];
}

const GROUPS: readonly Pick<
  MapFeatureMenuGroup,
  'geometryType' | 'name'
>[] = [
  { geometryType: 'place', name: 'Places' },
  { geometryType: 'area', name: 'Areas' },
  { geometryType: 'path', name: 'Paths' },
  { geometryType: 'grid', name: 'Grids' },
];

const COLLAPSED_GROUPS_STORAGE_KEY = 'map-feature-groups-collapsed@1';

@Component({
  selector: 'app-map-layer-menu',
  templateUrl: './map-layer-menu.component.html',
  styleUrls: ['./map-layer-menu.component.css'],
})
export class MapLayerMenuComponent implements OnDestroy {
  private readonly sessionSubscription;
  readonly view$ = this.featureManager.features$.pipe(
    map(features => {
      const groups = GROUPS.map(group => {
        const groupFeatures = features.filter(
          feature => feature.geometryType === group.geometryType,
        );
        return {
          ...group,
          features: groupFeatures.map(feature => this.toMenuItem(feature)),
        } satisfies MapFeatureMenuGroup;
      }).filter(group => group.features.length > 0);

      return {
        groups,
        visibleFeatures: groups.flatMap(group => group.features).filter(feature => feature.enabled),
      } satisfies MapFeatureMenuView;
    }),
  );

  @Input() compact = false;
  private readonly collapsedGroups = new Set<MapGeometryType>(
    this.readCollapsedGroups(),
  );

  constructor(
    private readonly featureManager: MapFeatureManagerService,
    private readonly storage: LocalStorageService,
    private readonly router: Router,
    private readonly auth: AuthService,
    readonly workspaceLayout: WorkspaceLayoutService,
  ) {
    this.sessionSubscription = storage.sessionChanged$.subscribe(() => {
      this.collapsedGroups.clear();
      for (const group of this.readCollapsedGroups()) this.collapsedGroups.add(group);
    });
  }

  ngOnDestroy(): void { this.sessionSubscription.unsubscribe(); }

  async openWorkspace(feature: MapFeatureMenuItem, mode: WorkspaceOpenMode = 'docked-right'): Promise<void> {
    if (!feature.page || !this.canOpenPage(feature)) return;
    // An active Workspace includes its collection/detail routes. Re-entering
    // the feature's default URL would leave that view (or hit its leave guard).
    if (this.isPageActive(feature)) {
      this.workspaceLayout.openAs(mode);
    } else if (!await this.router.navigateByUrl(feature.page, {
      info: workspaceNavigationInfo(mode),
    })) return;
  }

  canOpenPage(feature: MapFeatureMenuItem): boolean {
    if (!feature.page) return false;
    // Route data owns permissions; each matched parent may also guard its children.
    let routes: Routes = this.router.config;
    let remaining = feature.page.replace(/^\//, '');
    while (remaining) {
      const route = routes.find(candidate => candidate.path
        && (remaining === candidate.path || remaining.startsWith(candidate.path + '/')));
      if (!route?.path) return false;
      const required = route.data?.['permission'] as string | undefined;
      const anyOf = route.data?.['permissionAny'] as string[] | undefined;
      if (required || anyOf) {
        if (!(required && this.auth.hasPermission(required))
          && !anyOf?.some(permission => this.auth.hasPermission(permission))) return false;
      }
      remaining = remaining.slice(route.path.length).replace(/^\//, '');
      routes = route.children ?? [];
    }
    return true;
  }

  isPageActive(feature: MapFeatureMenuItem): boolean {
    if (!this.canOpenPage(feature)) return false;
    const path = this.routePath();
    let active: MapFeature | undefined;
    for (const item of this.featureManager.getFeatures()) {
      if (item.page && (path === item.page || path.startsWith(item.page + '/'))
        && item.page.length > (active?.page?.length ?? 0)) active = item;
    }
    return active?.key === feature.key;
  }

  private routePath(): string {
    return this.router.url.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  }

  toggleFeature(feature: MapFeatureMenuItem): void {
    this.featureManager.toggleFeature(feature.key);
  }

  toggleGroup(group: MapFeatureMenuGroup): void {
    if (this.collapsedGroups.has(group.geometryType)) {
      this.collapsedGroups.delete(group.geometryType);
    } else {
      this.collapsedGroups.add(group.geometryType);
    }
    this.storeCollapsedGroups();
  }

  isGroupCollapsed(geometryType: MapGeometryType): boolean {
    return this.collapsedGroups.has(geometryType);
  }

  trackByGeometryType(
    _index: number,
    group: MapFeatureMenuGroup,
  ): MapGeometryType {
    return group.geometryType;
  }

  trackByFeatureKey(_index: number, feature: MapFeatureMenuItem): string {
    return feature.key;
  }

  private toMenuItem(feature: MapFeature): MapFeatureMenuItem {
    return {
      key: feature.key,
      page: feature.page ?? null,
      name: feature.name,
      icon: feature.icon || 'layers',
      iconColor: feature.iconColor || null,
      enabled: feature.enabled,
      loading: feature.loading ?? false,
      count: feature.count ?? null,
    };
  }

  private readCollapsedGroups(): readonly MapGeometryType[] {
    try {
      const stored = this.storage.get<unknown>(COLLAPSED_GROUPS_STORAGE_KEY);
      if (!Array.isArray(stored)) return [];

      return GROUPS
        .map(group => group.geometryType)
        .filter(geometryType => stored.includes(geometryType));
    } catch {
      return [];
    }
  }

  private storeCollapsedGroups(): void {
    this.storage.set(COLLAPSED_GROUPS_STORAGE_KEY, [...this.collapsedGroups]);
  }
}
